import { ConfirmModal } from '@affine/component';
import type { ReactElement } from 'react';
import { useEffect, useMemo, useState } from 'react';

import type { ManagedDatabaseOwner } from './managed-database-owner';

export type {
  ManagedDatabaseOwner,
  ManagedDatabaseProtectionInput,
} from './managed-database-owner';
export {
  describeManagedDatabaseOwner,
  isManagedDatabaseProtected,
  readManagedDatabaseProtectionFromModel,
  resolveCustomAppDatabaseOwner,
} from './managed-database-owner';

export function ManagedDatabaseDeleteDialog({
  actionLabel,
  databaseTitle,
  description,
  onConfirm,
  onOpenChange,
  open,
  owner,
}: {
  actionLabel: string;
  databaseTitle: string;
  description: string;
  onConfirm: () => void | Promise<void>;
  onOpenChange: (open: boolean) => void;
  open: boolean;
  owner: ManagedDatabaseOwner | null;
}): ReactElement {
  const [confirmation, setConfirmation] = useState('');
  const canConfirm = confirmation === databaseTitle;
  const ownerLabel = owner?.label ?? 'Unbekannter Systembereich';

  useEffect(() => {
    if (!open) setConfirmation('');
  }, [open]);

  const help = useMemo(
    () =>
      `Diese Database ist systemverwaltet (${ownerLabel}). Löschen kann Apps oder Automationen beschädigen. Zur Bestätigung Database-Namen exakt eingeben.`,
    [ownerLabel]
  );

  return (
    <ConfirmModal
      open={open}
      onOpenChange={onOpenChange}
      title={`${actionLabel}: ${databaseTitle}`}
      description={description}
      confirmText={actionLabel}
      cancelText="Abbrechen"
      confirmButtonOptions={{ variant: 'error', disabled: !canConfirm }}
      onConfirm={() => {
        if (!canConfirm) return;
        return onConfirm();
      }}
      autoFocusConfirm={false}
    >
      <div style={{ display: 'grid', gap: 12 }}>
        <div
          style={{
            border: '1px solid var(--affine-border-color)',
            borderRadius: 8,
            padding: 12,
          }}
        >
          <div style={{ fontWeight: 600 }}>{ownerLabel}</div>
          <div style={{ marginTop: 6, color: 'var(--affine-text-secondary)' }}>
            {help}
          </div>
          {owner ? (
            <button
              type="button"
              onClick={owner.open}
              style={{
                marginTop: 10,
                border: '1px solid var(--affine-border-color)',
                borderRadius: 6,
                padding: '6px 10px',
              }}
            >
              {owner.openLabel}
            </button>
          ) : null}
        </div>
        <label style={{ display: 'grid', gap: 6 }}>
          <span>Database-Name</span>
          <input
            value={confirmation}
            onChange={event => setConfirmation(event.target.value)}
            placeholder={databaseTitle}
            style={{
              border: '1px solid var(--affine-border-color)',
              borderRadius: 6,
              padding: '8px 10px',
            }}
          />
        </label>
      </div>
    </ConfirmModal>
  );
}
